import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlCreationOptions } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, first, map, withLatestFrom } from 'rxjs';
import { onUntouched } from '../../shared/loading-state/loadable';
import { getCompanies, getUserPermissions, setSelectedCompany } from '../store/auth.actions';
import { selectAccount, selectCompanies, selectSelectedCompany, selectUserProfile } from '../store/auth.selectors';
import { CompanyDataType } from './auth.models';
import { AuthService } from './auth.service';

/**
 * Account Guard collects and synchronizes Client (ei. Account / Company) parameter from URL string,
 * checks if the current user has access to the account, and checks if the account.
 *
 * If user has no access or an error occurred, user will be denied access to the page
 * and redirected to Account Selection screen.
 *
 * If user has access, but the account has no active subscription, user will be redirected to the
 * corresponding subscription selection screen.
 *
 * @author Anton Valeev
 */
export const hasAccountAccess: CanActivateFn = (routeSnapshot, state) => {
  const store = inject(Store);
  const auth = inject(AuthService);
  const router = inject(Router);

  return combineLatest([store.select(selectCompanies), store.select(selectUserProfile)]).pipe(
    filter(([, user]) => !!user),
    onUntouched(() => store.dispatch(getCompanies()), 0),
    first(([c]) => !!c.value),
    withLatestFrom(store.select(selectSelectedCompany)),
    map(([[c], selected]) => {
      const companies = c.value!;

      // If there's a client parameter, check if it's valid and set it as selected account
      const clientParam = routeSnapshot.queryParamMap.get('c') ?? routeSnapshot.queryParamMap.get('client');
      const company =
        companies.find(c_ => c_.company_name === (clientParam ?? auth.selectedAccount)) ??
        companies[0];

      if (!company) return false;

      if (company.company_name !== selected?.company_name) {
        auth.selectedAccount = company.company_name;
        store.dispatch(setSelectedCompany({ company }));
        store.dispatch(getUserPermissions());
      }

      if (!clientParam) {
        const queryParams = {
          ...routeSnapshot.queryParams,
          c: company.company_name,
        } as Record<string, string>;

        if (router.url.startsWith('/auth/sso')) {
          delete queryParams['code'];
          delete queryParams['issuer'];
          delete queryParams['state'];
        }

        delete queryParams['company'];

        return router.createUrlTree([state.url.split('?')[0]], { queryParams });
      }

      return true;
    })
  );
};

/**
 * Reroutes navigation to a fallback location
 * if currently selected account's `data_type` is different from the one specified.
 */
export const forAccountType = (
  accountType: CompanyDataType[],
  fallback: readonly any[],
  navigationExtras?: UrlCreationOptions
): CanActivateFn => {
  return () => {
    const router = inject(Router);
    const store = inject(Store);

    return store.select(selectAccount).pipe(
      first(account => !!account),
      map(account => {
        if (accountType.includes(account.data_type)) return true;
        return router.createUrlTree(fallback, navigationExtras);
      })
    );
  };
};

/**
 * Redirects from the current route to the one specified through the `commands` parameter
 * when current user has only one account
 */
export const redirectOnSingleAccount = (
  commands: readonly any[],
  navigationExtras?: UrlCreationOptions
): CanActivateFn => {
  return () => {
    const router = inject(Router);
    const store = inject(Store);

    return store.select(selectCompanies).pipe(
      first(accounts => !accounts.isLoading && !!accounts.value),
      map(accounts => {
        if (accounts.value!.length === 1) return router.createUrlTree(commands, navigationExtras);
        return true;
      })
    );
  };
};
